import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { Space } from "components/icons/generated";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoBoolean from "components/MetaInfoList/Boolean";
import MetaInfoListItem from "components/MetaInfoList/Item";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import Link from "ds/components/Link";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import useCanManageWebhook from "shared/Webhook/access/useCanManageWebhook";
import { NamedWebhooksIntegration } from "types/generated";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { showDeleteConfirmation } from "views/Account/Integrations/registry/Webhooks/DeleteConfirmation";
import WebhookOptionDropdown from "views/Account/Integrations/registry/Webhooks/OptionsDropdown";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import { showWebhookDetailsDrawer } from "../DetailsDrawer";
import styles from "./styles.module.css";

const useIntegrationsLayout = (webhook?: NamedWebhooksIntegration) => {
  const navigate = useNavigate();
  const { integration } = useOutletContext<IntegrationsOutletContext>();
  const { integrationId: webhookId } = useParams<{
    integrationId: string;
  }>();

  const handleDeleteClick = () =>
    webhook &&
    showDeleteConfirmation({ name: webhook.name, id: webhook.id }).then(() => {
      navigate("/integrations/webhooks", { replace: true });
    });

  const hasManageAccess = useCanManageWebhook()(webhook);

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: integration.name,
      link: `/integrations/${integration.id}`,
    },
    {
      title: webhookId ?? "",
    },
  ]);

  useLayoutHeading(webhookId ?? "Webhook");

  useLayoutActions(
    webhook ? (
      <>
        <Button variant="secondary" onPress={() => showWebhookDetailsDrawer({ webhook })}>
          Details
        </Button>
        <WebhookOptionDropdown
          id={webhook.id}
          hasManageAccess={hasManageAccess}
          onDeleteClick={handleDeleteClick}
          dotSize="medium"
        />
      </>
    ) : null
  );

  useLayoutMeta(
    webhook ? (
      <>
        <ViewHeaderWrapper direction="row" fullWidth>
          <Typography variant="p-body2" tag="span" className={styles.link}>
            Webhook to&nbsp;
            <TextEllipsis tooltip={webhook?.endpoint} tooltipWidthMode="maxWidthSm">
              {(props) => (
                <Link href={webhook?.endpoint} target="_blank" {...props}>
                  {webhook?.endpoint}
                </Link>
              )}
            </TextEllipsis>
          </Typography>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            {webhook?.space && (
              <MetaInfoListItem
                icon={Space}
                linkText={webhook.space.name}
                href={`/spaces/${webhook.space.id}`}
              />
            )}
            <MetaInfoBoolean
              text={webhook?.enabled ? "Enabled" : "Disabled"}
              value={webhook?.enabled}
            />
            <TimestampMetaInfo timestamp={webhook?.createdAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </>
    ) : null
  );
};

export default useIntegrationsLayout;
